import type {
  CreditBankReason,
  CreditBankSourceType,
} from '@/modules/account/types'
import { cn, formatNumber, humanizeText } from '@/utils'
import type { CreditEntryGroup } from './groupEntries'

const REASON_LABELS: Record<CreditBankReason, string> = {
  plan_grant: 'Plan credits',
  overage_purchase: 'Credit purchase',
  project_grant: 'Project grant',
  admin_adjust: 'Adjustment',
  consumption: 'Usage',
  expiry: 'Expired',
}

const SOURCE_NOUNS: Record<CreditBankSourceType, [string, string]> = {
  statement: ['statement', 'statements'],
  screening: ['screening', 'screenings'],
  checkout: ['purchase', 'purchases'],
  expiry: ['expiry', 'expiries'],
}

export const describeReason = (reason: CreditBankReason) =>
  REASON_LABELS[reason] ?? humanizeText(reason)

export const formatDelta = (deltaCredits: number) =>
  `${deltaCredits > 0 ? '+' : ''}${formatNumber(deltaCredits)}`

export const amountClass = (deltaCredits: number) =>
  cn(
    'text-right font-medium tabular-nums text-emerald-500',
    deltaCredits < 0 && 'text-red-600',
  )

const countNoun = (sourceType: CreditBankSourceType | null, count: number) => {
  if (!sourceType) return count === 1 ? null : `${count} entries`
  const [singular, plural] = SOURCE_NOUNS[sourceType]
  return `${count} ${count === 1 ? singular : plural}`
}

export const describeGroup = (group: CreditEntryGroup) =>
  [
    describeReason(group.reason),
    group.project?.name,
    countNoun(group.sourceType, group.entries.length),
  ]
    .filter(Boolean)
    .join(' · ')

export const hasDetails = (group: CreditEntryGroup) =>
  group.entries.some(
    (entry) => entry.source !== null && entry.source !== undefined,
  )
